import React, { useState, useEffect, useImperativeHandle } from 'react';
import { Container } from 'konva/lib/Container';
import { KonvaEventObject, Node, NodeConfig } from "konva/lib/Node";
import { Line } from 'react-konva';
import { IGuidesRef } from '../../types';
import { IProps, IState, IStopAttributes, IGuideAttributes, IFinalGuides, IVHLineGuide, IVHLineGuideStop, IMargins, IOrientation, IStop, ILineGuide, IAttributes } from './types';
import * as S from './styles';

const initGuideLineProps = {
  stroke: "#ee3434",
  strokeWidth: 1,
  name: "guid-line",
  dash: [4, 6],
}

const GuidesAddIn = React.forwardRef<IGuidesRef, IProps>((props, ref) => {

  const GUIDELINE_OFFSET = 3;
  const [guides, setGuides] = useState<IState['guides'][]>([]);

  // where can we snap our objects?
  const getLineGuideStops = (skipShape: KonvaEventObject<DragEvent>['target']) => {
    // we can snap to stage borders and the center of the stage
    if (props.stage) {
      let lineGuideStops: IVHLineGuideStop[] = [];
      let stopAttributes: IStopAttributes[] = [];
      const artboard = props.artboards.find(artboard => `${artboard.id}-object` === skipShape.attrs.name);
      if (artboard) {
        stopAttributes = [...stopAttributes, {type: "ArtboardLine", x: artboard.attributes.x, y: artboard.attributes.y, width: artboard.attributes.width, height: artboard.attributes.height}];
      } else {
        stopAttributes = [...stopAttributes, {type: "ArtboardLine", x: 0, y: 0, width: props.stage.width(), height: props.stage.height()}];
      }
      // and we snap over edges and center of each object on the canvas
      props.stage.find(`.${skipShape.attrs.name}`).map((guideItem) => {
        if (guideItem.id() === skipShape.id() || !guideItem.visible()) return;
        const containerStage: Container<Node<NodeConfig>> | any = props.stage;
        var box = guideItem.getClientRect({relativeTo: containerStage});
        // and we can snap to all edges of shapes
        stopAttributes = [...stopAttributes, {type: "ItemLine", x: box.x, y: box.y, width: box.width, height: box.height}];
      });
      stopAttributes.map(stopAttribute => {
        const type = stopAttribute.type;
        const x = stopAttribute.x;
        const y = stopAttribute.y;
        const width = stopAttribute.width;
        const height = stopAttribute.height;
        lineGuideStops = [...lineGuideStops, {
          margins: {left: x, top: y, right: x + width, bottom: y + height},
          vertical: [{type, value: x}, {type, value: x + width/2}, {type, value: x + width}],
          horizontal: [{type, value: y}, {type, value: y + height/2}, {type, value: y + height}]
        }];
      })
      return lineGuideStops;
    } else {
      return [];
    }
  };

  // what points of the object will trigger to snapping?
  // it can be just center of the object
  // but we will enable all edges and center
  const getObjectSnappingEdges = (node: KonvaEventObject<DragEvent>['target']): IVHLineGuide => {
    const containerStage: Container<Node<NodeConfig>> | any = props.stage;
    var box = node.getClientRect({relativeTo: containerStage});
    return {
      margins: {left: box.x, top: box.y, right: box.x + box.width, bottom: box.y + box.height},
      vertical: [
        {
          guide: box.x,
          snap: "start"
        },
        {
          guide: box.x + box.width / 2,
          snap: "center"
        },
        {
          guide: box.x + box.width,
          snap: "end"
        }
      ],
      horizontal: [
        {
          guide: box.y,
          snap: "start"
        },
        {
          guide: box.y + box.height / 2,
          snap: "center"
        },
        {
          guide: box.y + box.height,
          snap: "end"
        }
      ]
    };
  };

  const resolveGuides = (itemMargins: IMargins, stopMargins: IMargins, stop: IStop, lineGuide: ILineGuide) => {
    var resultVH: IGuideAttributes[] = [];
    var diff = stop.value - lineGuide.guide;
    var absDiff = Math.abs(diff);
    // if the distance between guild line and object snap point is close we can consider this for snapping
    if (absDiff < GUIDELINE_OFFSET) {
      resultVH = [...resultVH, {
        type: stop.type,
        lineGuide: stop.value,
        diff: diff,
        absDiff: absDiff,
        snap: lineGuide.snap,
        margins: {
          left: Math.min(itemMargins.left, stopMargins.left),
          top: Math.min(itemMargins.top, stopMargins.top),
          right: Math.max(itemMargins.right, stopMargins.right),  
          bottom: Math.max(itemMargins.bottom, stopMargins.bottom)
        },
      }];
    }
    return resultVH;
  }

  // find all snapping possibilities
  const getGuides = ( vhLineGuideStops: IVHLineGuideStop[], vhLineGuide: IVHLineGuide) => {
    var resultV: IGuideAttributes[] = [];
    var resultH: IGuideAttributes[] = [];
    vhLineGuideStops.map(vhLineGuideStop => {
      vhLineGuideStop.vertical.map(stop => {
        vhLineGuide.vertical.map(lineGuide => {
          resultV = [...resultV, ...resolveGuides(vhLineGuide.margins, vhLineGuideStop.margins, stop, lineGuide)];
        });
      })
      vhLineGuideStop.horizontal.map(stop => {
        vhLineGuide.horizontal.map(lineGuide => {
          resultH = [...resultH, ...resolveGuides(vhLineGuide.margins, vhLineGuideStop.margins, stop, lineGuide)];
        });
      });
    });
    let guides: IFinalGuides[] = [];
    // find closest snap
    guides = [...guides, ...validateGuides(resultV, "V")];
    guides = [...guides, ...validateGuides(resultH, "H")];
    return guides;
  };

  const validateGuides = (resultVH: IGuideAttributes[], orientation: IOrientation): IFinalGuides[] => {
    let guides: IFinalGuides[] = [];
    resultVH.sort((a, b) => a.absDiff - b.absDiff).map((minVH, indexVH) => {
      guides = [...guides, {
        type: minVH.type,
        lineGuide: minVH.lineGuide,
        diff: minVH.diff,
        orientation: orientation,
        snap: minVH.snap,
        snapeable: indexVH == 0 ? true : false,
        margins: minVH.margins,
      }];
    });
    return guides;
  }

  const drawGuides = (detectedGuides: IFinalGuides[], vDiff: number, hDiff: number) => {
    let newGuides: IState['guides'][] = [];
    if (detectedGuides) {
      detectedGuides.map(lg => {
        let guideLineProps = {...initGuideLineProps};
        if (lg.type === "ArtboardLine") guideLineProps.dash = [];
        if (lg.orientation === "H" && lg.diff === hDiff) {
          let guide = {
            ...guideLineProps,
            points: [lg.margins.left ?? -6000, lg.lineGuide, lg.margins.right ?? 6000, lg.lineGuide],
          };
          newGuides = [...newGuides, guide];
        } else if (lg.orientation === "V" && lg.diff === vDiff) {
          let guide = {
            ...guideLineProps,
            points: [lg.lineGuide, lg.margins.top ?? -6000, lg.lineGuide, lg.margins.bottom ?? 6000],
          };
          newGuides = [...newGuides, guide];
        }
      });
    }
    setGuides(newGuides);
  };

  useImperativeHandle(
    ref,
    () => ({
      onDragMove(e: KonvaEventObject<DragEvent>) {
        if (e.target.attrs.name !== undefined && e.target && props.selectedElementsId.length === 1) {
          // find possible snapping lines
          var lineGuideStops = getLineGuideStops(e.target);
          // find snapping points of current object
          var itemBounds = getObjectSnappingEdges(e.target);
          // now find where can we snap current object
          var guides = getGuides(lineGuideStops, itemBounds);
          // do nothing of no snapping
          if (!guides.length) {
            setGuides([]);
            return;
          }
          // now force object position
          let vDiff = 0;
          let hDiff = 0;
          guides.filter(lg => lg.snapeable === true).map(lg => {
            if (lg.orientation === "V") {
              e.target.x(e.target.x() + lg.diff);
              vDiff = lg.diff;
            } else {
              e.target.y(e.target.y() + lg.diff);
              hDiff = lg.diff;
            }
          });
          drawGuides(guides, vDiff, hDiff);
        }
      },
      onDragEnd() {
        setGuides([]);
      },
    }),
  );

  return (
    <>
      { 
        guides.map((item, i: number) => {
          return <Line key={i} {...item} />;
        })
      }
    </>
  );
});
  
export default GuidesAddIn;